import { useState, useEffect } from "react";
import { suppliersApi, departmentsApi, type Supplier, type Department, type SupplierDepartment, type BBBEEStatus, ApiError } from "@/lib/api";

const BBBEE_OPTIONS: BBBEEStatus[] = ["Level 1", "Level 2", "Level 3", "Level 4", "Level 5", "Level 6", "Level 7", "Level 8", "Non-Compliant", "Exempt (EME)"];

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" });
}

function taxClearanceStatus(dateStr: string | null): { label: string; bg: string; text: string } | null {
  if (!dateStr) return null;
  const days = (new Date(dateStr).getTime() - Date.now()) / (1000 * 60 * 60 * 24);
  if (days < 0) return { label: "Expired", bg: "bg-accent-50", text: "text-accent-700" };
  if (days <= 30) return { label: "Expiring Soon", bg: "bg-accent-50", text: "text-accent-700" };
  return { label: "Valid", bg: "bg-secondary-50", text: "text-secondary-700" };
}

interface ComplianceFormState {
  vendor_number: string;
  bbbee_status: string;
  tax_pin: string;
  tax_clearance_validity: string;
  csd_number: string;
}

function toFormState(supplier: Supplier): ComplianceFormState {
  return {
    vendor_number: supplier.vendor_number ?? "",
    bbbee_status: supplier.bbbee_status ?? "",
    tax_pin: supplier.tax_pin ?? "",
    tax_clearance_validity: supplier.tax_clearance_validity ? supplier.tax_clearance_validity.slice(0, 10) : "",
    csd_number: supplier.csd_number ?? "",
  };
}

export default function ComplianceCard({ supplier, onUpdated }: { supplier: Supplier; onUpdated: (s: Supplier) => void }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<ComplianceFormState>(toFormState(supplier));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [departments, setDepartments] = useState<Department[]>([]);
  const [registrations, setRegistrations] = useState<SupplierDepartment[]>([]);
  const [loadingRegs, setLoadingRegs] = useState(true);
  const [showAddDept, setShowAddDept] = useState(false);
  const [newDeptId, setNewDeptId] = useState<number | "">("");
  const [newDeptVendorNumber, setNewDeptVendorNumber] = useState("");
  const [savingDept, setSavingDept] = useState(false);

  useEffect(() => {
    setForm(toFormState(supplier));
  }, [supplier]);

  useEffect(() => {
    Promise.all([departmentsApi.list(), suppliersApi.departments(supplier.id)])
      .then(([deptRes, regRes]) => {
        setDepartments(deptRes.data.data);
        setRegistrations(regRes.data);
      })
      .catch(() => {})
      .finally(() => setLoadingRegs(false));
  }, [supplier.id]);

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const res = await suppliersApi.update(supplier.id, {
        vendor_number: form.vendor_number || null,
        bbbee_status: (form.bbbee_status || null) as BBBEEStatus | null,
        tax_pin: form.tax_pin || null,
        tax_clearance_validity: form.tax_clearance_validity || null,
        csd_number: form.csd_number || null,
      });
      onUpdated(res.data);
      setEditing(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to save compliance details");
    } finally {
      setSaving(false);
    }
  };

  const handleAddDepartment = async () => {
    if (!newDeptId) return;
    setSavingDept(true);
    setError("");
    try {
      const res = await suppliersApi.setDepartmentVendorNumber(supplier.id, Number(newDeptId), newDeptVendorNumber);
      setRegistrations(res.data);
      setShowAddDept(false);
      setNewDeptId("");
      setNewDeptVendorNumber("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to add department registration");
    } finally {
      setSavingDept(false);
    }
  };

  const handleRemoveDepartment = async (departmentId: number) => {
    setError("");
    try {
      await suppliersApi.removeDepartment(supplier.id, departmentId);
      setRegistrations((prev) => prev.filter((d) => d.id !== departmentId));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to remove department registration");
    }
  };

  const taxStatus = taxClearanceStatus(supplier.tax_clearance_validity);
  const availableDepartments = departments.filter((d) => !registrations.some((r) => r.id === d.id));

  return (
    <div className="bg-white rounded-lg border border-background-200 p-4 md:p-5 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-primary-600 text-base font-semibold">Compliance &amp; Registration</h2>
        {!editing && (
          <button onClick={() => setEditing(true)}
            className="h-8 px-3 border border-background-300 text-foreground-600 text-xs font-medium rounded-md hover:bg-background-100 transition-colors whitespace-nowrap flex items-center gap-1.5 cursor-pointer">
            <i className="ri-edit-line text-sm"></i><span>Edit</span>
          </button>
        )}
      </div>

      {error && (
        <div className="flex items-start gap-2.5 bg-accent-50 border border-accent-200 rounded-lg px-3.5 py-3 mb-4">
          <i className="ri-error-warning-line text-accent-600 text-sm mt-0.5 shrink-0"></i>
          <p className="text-accent-700 text-sm">{error}</p>
        </div>
      )}

      {editing ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-foreground-600 text-xs font-semibold uppercase tracking-wider mb-1.5">Vendor Number</label>
              <input value={form.vendor_number} onChange={(e) => setForm({ ...form, vendor_number: e.target.value })}
                className="form-input" placeholder="e.g. V-00123" />
            </div>
            <div>
              <label className="block text-foreground-600 text-xs font-semibold uppercase tracking-wider mb-1.5">B-BBEE Status</label>
              <select value={form.bbbee_status} onChange={(e) => setForm({ ...form, bbbee_status: e.target.value })} className="form-input">
                <option value="">—</option>
                {BBBEE_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-foreground-600 text-xs font-semibold uppercase tracking-wider mb-1.5">Tax PIN</label>
              <input value={form.tax_pin} onChange={(e) => setForm({ ...form, tax_pin: e.target.value })}
                className="form-input" placeholder="SARS Tax Compliance PIN" />
            </div>
            <div>
              <label className="block text-foreground-600 text-xs font-semibold uppercase tracking-wider mb-1.5">Tax Clearance Validity</label>
              <input type="date" value={form.tax_clearance_validity} onChange={(e) => setForm({ ...form, tax_clearance_validity: e.target.value })}
                className="form-input" />
            </div>
            <div>
              <label className="block text-foreground-600 text-xs font-semibold uppercase tracking-wider mb-1.5">CSD Number</label>
              <input value={form.csd_number} onChange={(e) => setForm({ ...form, csd_number: e.target.value })}
                className="form-input" placeholder="e.g. MAAA0123456" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleSave} disabled={saving}
              className="h-9 px-4 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-md disabled:opacity-60 transition-colors cursor-pointer flex items-center gap-2">
              {saving && <i className="ri-loader-4-line animate-spin text-sm"></i>}
              Save
            </button>
            <button onClick={() => { setEditing(false); setForm(toFormState(supplier)); setError(""); }}
              className="h-9 px-4 border border-background-300 text-foreground-600 text-sm font-medium rounded-md hover:bg-background-100 transition-colors cursor-pointer">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-2">
          <div>
            <p className="text-foreground-400 text-xs font-medium uppercase tracking-wider mb-1">Vendor Number</p>
            <p className="text-foreground-800 text-sm font-semibold">{supplier.vendor_number || "—"}</p>
          </div>
          <div>
            <p className="text-foreground-400 text-xs font-medium uppercase tracking-wider mb-1">B-BBEE Status</p>
            <p className="text-foreground-800 text-sm font-semibold">{supplier.bbbee_status || "—"}</p>
          </div>
          <div>
            <p className="text-foreground-400 text-xs font-medium uppercase tracking-wider mb-1">Tax PIN</p>
            <p className="text-foreground-800 text-sm font-semibold">{supplier.tax_pin || "—"}</p>
          </div>
          <div>
            <p className="text-foreground-400 text-xs font-medium uppercase tracking-wider mb-1">Tax Clearance Validity</p>
            <div className="flex items-center gap-2">
              <p className="text-foreground-800 text-sm font-semibold">{formatDate(supplier.tax_clearance_validity)}</p>
              {taxStatus && (
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${taxStatus.bg} ${taxStatus.text}`}>
                  {taxStatus.label}
                </span>
              )}
            </div>
          </div>
          <div>
            <p className="text-foreground-400 text-xs font-medium uppercase tracking-wider mb-1">CSD Number</p>
            <p className="text-foreground-800 text-sm font-semibold">{supplier.csd_number || "—"}</p>
          </div>
        </div>
      )}

      {/* Department registrations */}
      <div className="mt-6 pt-5 border-t border-background-200">
        <div className="flex items-center justify-between mb-3">
          <p className="text-foreground-600 text-xs font-semibold uppercase tracking-wider">Department Registrations</p>
          {!showAddDept && availableDepartments.length > 0 && (
            <button onClick={() => setShowAddDept(true)}
              className="h-7 px-2.5 border border-background-300 text-foreground-600 text-xs font-medium rounded-md hover:bg-background-100 transition-colors cursor-pointer flex items-center gap-1">
              <i className="ri-add-line text-xs"></i><span>Add</span>
            </button>
          )}
        </div>

        {showAddDept && (
          <div className="flex flex-wrap items-end gap-2 mb-3 bg-background-50 rounded-lg p-3">
            <div>
              <label className="block text-foreground-500 text-[11px] font-medium mb-1">Department</label>
              <select value={newDeptId} onChange={(e) => setNewDeptId(e.target.value ? Number(e.target.value) : "")}
                className="h-9 px-2.5 border border-background-300 rounded-md text-sm bg-white">
                <option value="">Select department…</option>
                {availableDepartments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-foreground-500 text-[11px] font-medium mb-1">Vendor Number</label>
              <input value={newDeptVendorNumber} onChange={(e) => setNewDeptVendorNumber(e.target.value)}
                placeholder="Department vendor number" className="h-9 px-2.5 border border-background-300 rounded-md text-sm w-48" />
            </div>
            <button onClick={handleAddDepartment} disabled={!newDeptId || savingDept}
              className="h-9 px-3 bg-primary-600 hover:bg-primary-700 text-white text-xs font-semibold rounded-md disabled:opacity-60 cursor-pointer">
              {savingDept ? "Saving…" : "Save"}
            </button>
            <button onClick={() => { setShowAddDept(false); setNewDeptId(""); setNewDeptVendorNumber(""); }}
              className="h-9 px-3 border border-background-300 text-foreground-600 text-xs font-medium rounded-md hover:bg-background-100 cursor-pointer">
              Cancel
            </button>
          </div>
        )}

        {loadingRegs ? (
          <p className="text-foreground-400 text-sm">Loading…</p>
        ) : registrations.length === 0 ? (
          <p className="text-foreground-400 text-sm">Not registered with any department yet.</p>
        ) : (
          <div className="space-y-1.5">
            {registrations.map((reg) => (
              <div key={reg.id} className="flex items-center justify-between px-3 py-2 bg-background-50 rounded-md">
                <span className="text-foreground-800 text-sm font-medium">{reg.name}</span>
                <div className="flex items-center gap-3">
                  <span className="text-foreground-500 text-xs">{reg.pivot.vendor_number || "—"}</span>
                  <button onClick={() => handleRemoveDepartment(reg.id)}
                    className="text-foreground-400 hover:text-accent-600 transition-colors cursor-pointer">
                    <i className="ri-close-line text-sm"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
